// The small pieces of markup every page is built from: names, art, links,
// section heads, tabs, the pager and stats. No figures (see figures.ts) and
// nothing about any one page. Nothing here touches the DOM.

import type { ActiveRow, Paging, RankedRow, TagValue } from "../../shared/api.ts";
import { escapeHtml, headerSrc } from "./web.ts";

// A game as a list row, in any list: ranked, or (search, a company's) maybe
// not, with its tag's value on a tag's list. What appRow and its parts read.
export type Row = (ActiveRow | (RankedRow & { active?: boolean })) & { value?: TagValue };

// A game anything here names: a row, or the game a page is about.
type Named = { app_id: number; name: string | null };

// A paged list as pagedMeta and pagedBody read it.
export type PagedList<R> = Paging & { apps: R[] };

// "1 day", "2 days": `word` as `n` needs it, without the number.
export const plural = (n: number, word: string): string => (n === 1 ? word : `${word}s`);

// A link that opens off-site in a new tab, marked with an arrow.
export function externalLink(href: string, text: string): string {
  return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener">${escapeHtml(
    text
  )} ↗</a>`;
}

export function displayName(game: Named): string {
  return game.name || `App ${game.app_id}`;
}

// By characters, not UTF-16 units, so an emoji or other astral character is
// never split in half.
export function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);

  return words.length > 1
    ? words.slice(0, 3).map((w) => Array.from(w)[0] ?? "").join("").toUpperCase()
    : Array.from(name.trim()).slice(0, 4).join("");
}

// A game's header image (headerSrc). If it fails, app.js shows the label
// instead.
export function art(game: Named & { header_image: string | null }, name: string): string {
  const src = headerSrc(game);

  return `
    <span class="art" data-label="${escapeHtml(initials(name))}">
      <img src="${escapeHtml(src)}" alt="" loading="lazy">
    </span>`;
}

// Pages are numbered from 0, matching the API; page 0 lives at the list's
// own path ("/", or a tag page's), later pages at ?page=N. Readers see them
// numbered from 1 (pageLabel).
// A path with a query of its own (the search page's) takes it after that.
export const pageHref = (page: number, path = "/"): string =>
  page === 0 ? path : `${path}${path.includes("?") ? "&" : "?"}page=${page}`;

// A link back to a list: by default, a game page's to the list page it came
// from.
export const backLinkHtml = (href = "/", label = "All games"): string =>
  `<a class="back" href="${href}" data-nav>← ${label}</a>`;

// A page's number as readers see it: page 0 is "1".
export const pageLabel = (page: number): number => page + 1;

// A section's head: its `title`, a muted `blurb` (HTML) beside or under it,
// and optionally a `more` link across from them.
export const sectionHead = (title: string, blurb: string, more = ""): string => `
  <div class="section-head${more && " with-more"}">
    <div class="section-title"><h2>${title}</h2><p class="muted">${blurb}</p></div>
    ${more}
  </div>`;

// Links between a page's siblings (a tag's values, a company's roles) as
// tabs, labelled `label`: [{ href, text, current }]. Nothing for a page with
// no siblings, as a lone tab leads nowhere.
export const tabs = (label: string, items: { href: string; text: string; current: boolean }[]): string =>
  items.length < 2 ? "" : `<nav class="tag-tabs" aria-label="${label}">${items
  .map(
    ({ href, text, current }) => `<a href="${href}" data-nav
      ${current ? 'aria-current="page"' : ""}>${text}</a>`
  )
  .join("")}</nav>`;

// Previous/next plus page numbers: the first, the last, and two either side
// of the current page, with an ellipsis for each gap.
export function pager(page: number, pages: number, path = "/"): string {
  if (pages <= 1) return "";

  const last = pages - 1;
  const step = (target: number, label: string, rel: string): string =>
    target >= 0 && target <= last
      ? `<a href="${pageHref(target, path)}" rel="${rel}" data-nav>${label}</a>`
      : `<span class="disabled">${label}</span>`;

  let items = step(page - 1, "← Prev", "prev");
  let shown = -1;

  for (let p = 0; p <= last; p++) {
    if (p !== 0 && p !== last && Math.abs(p - page) > 2) continue;

    if (p - shown > 1) items += `<span class="gap">…</span>`;
    items +=
      p === page
        ? `<span aria-current="page">${pageLabel(p)}</span>`
        : `<a href="${pageHref(p, path)}" data-nav>${pageLabel(p)}</a>`;
    shown = p;
  }

  items += step(page + 1, "Next →", "next");

  return `<nav class="pager" aria-label="Pages">${items}</nav>`;
}

// One of the chart's stats. Every stat keeps its note line, even empty, so
// they're all one height.
export const stat = (label: string, value: string, note = ""): string => `
  <div>
    <div class="stat-label">${label}</div>
    <div class="stat-value">${value}<small>${note || "&nbsp;"}</small></div>
  </div>`;
